import { useState } from 'react';
import { auth } from '../config/firebase';
import { PersonaGenerationResponse } from '../types/personas';
import { logger } from '../utils/logger';

// The base API URL
const API_URL = import.meta.env.VITE_API_URL || 'https://api.socialness.ai';

// Helper: dev-only log
const devLog = (...args: any[]) => {
  if (import.meta.env.DEV) logger.debug('PersonaGeneration', args.join(' '));
};

/**
 * Convert language code to 2-letter ISO 639-1 format
 * Examples: "fr-FR" -> "fr", "en-US" -> "en", "fr" -> "fr"
 */
const normalizeLanguageCode = (language: string): string => {
  if (!language) return 'en';
  
  // Extract the first 2 characters (language code) and convert to lowercase
  const normalized = language.substring(0, 2).toLowerCase();
  
  // Validate that it's a valid 2-letter code
  if (normalized.length === 2 && /^[a-z]{2}$/.test(normalized)) {
    return normalized;
  }
  
  // Fallback to English if invalid
  return 'en';
};

/**
 * Get user-friendly error message based on error type
 */
const getUserFriendlyErrorMessage = (error: any): string => {
  if (error instanceof TypeError && error.message === 'Load failed') {
    return 'Network connection failed. Please check your internet connection and try again.';
  }
  
  if (error.status) {
    switch (error.status) {
      case 400:
        // Check if we have detailed error data from the API
        if (error.errorData?.message) {
          return `Invalid request: ${error.errorData.message}`;
        }
        return 'The request contains invalid data. Please try again or contact support if the problem persists.';
      case 401:
        return 'Your session has expired. Please log out and log back in to continue.';
      case 403:
        return 'You do not have permission to generate personas. Please contact support.';
      case 404:
        return 'The persona generation service is currently unavailable. Please try again later.';
      case 429:
        return 'Too many requests. Please wait a moment before trying again.';
      case 500:
        return 'A server error occurred. Our team has been notified. Please try again later.';
      case 503:
        return 'The service is temporarily unavailable. Please try again in a few minutes.';
      default:
        return error.message || 'An unexpected error occurred. Please try again.';
    }
  }
  
  if (error.message) {
    return error.message;
  }
  
  return 'An unexpected error occurred. Please try again.';
};

/**
 * Custom hook for generating personas with AI
 * This directly uses fetch instead of React Query to avoid caching issues
 */
export const usePersonaGeneration = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  // Create a function that will handle the API call with retry logic
  const mutate = async (
    variables: { brandId: string; language: string },
    options?: {
      onSuccess?: (data: PersonaGenerationResponse) => void;
      onError?: (error: Error) => void;
    }
  ) => {
    const executeRequest = async (attempt: number = 1): Promise<any> => {
      devLog(`🔍 Persona Generation - Attempt ${attempt}/3`);
      
      // Get the Firebase ID token with force refresh to ensure it's valid
      const token = await auth.currentUser?.getIdToken(true); // Force refresh
      if (!token) {
        throw new Error('No authentication token available');
      }
      
      devLog('🔍 Persona Generation - Firebase token length:', token.length);
      devLog('🔍 Persona Generation - Firebase token starts with:', token.substring(0, 50) + '...');
      devLog('🔍 Persona Generation - Firebase token ends with:', '...' + token.substring(token.length - 50));
      devLog('🔍 Persona Generation - Firebase user:', auth.currentUser?.uid);
      devLog('🔍 Persona Generation - Firebase user email:', auth.currentUser?.email);
      devLog('🔍 Persona Generation - Full token for comparison:', token);
      
      // Extract variables and validate input data
      const { brandId, language } = variables;
      
      // Enhanced validation
      if (!brandId) {
        throw new Error('Brand ID is required for persona generation');
      }
      
      if (typeof brandId !== 'string') {
        throw new Error(`Brand ID must be a string, received: ${typeof brandId}`);
      }
      
      if (brandId.trim().length === 0) {
        throw new Error('Brand ID cannot be empty');
      }
      
      if (!language) {
        throw new Error('Language is required for persona generation');
      }
      
      devLog(`🚀 Starting AI persona generation for brand ID: ${brandId}`);

      // WARM-UP REQUEST: Try to make a simple request to "wake up" the server/session
      if (attempt === 1) {
        try {
          devLog('🔥 Performing warm-up request to prepare server...');
          const warmupUrl = `${API_URL}/management/personas?brandId=${brandId}&withAnalysis=false&appLanguage=en`;
          const warmupResponse = await fetch(warmupUrl, {
            method: 'GET',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          });
          devLog('🔥 Warm-up response status:', warmupResponse.status);
          // We don't care if this fails, it's just to wake up the server
          if (warmupResponse.ok) {
            devLog('✅ Warm-up successful');
          } else {
            devLog('⚠️ Warm-up failed but continuing...');
          }
          // Small delay to let the server process
          await new Promise(resolve => setTimeout(resolve, 500));
        } catch (warmupError) {
          devLog('⚠️ Warm-up request failed but continuing:', warmupError);
        }
      }
      
      // Add timestamp to prevent caching
      const timestamp = Date.now();
      const url = `${API_URL}/ai-coach/personas/generate?timestamp=${timestamp}`;
      
      const requestBody = {
        brandId,
        language: normalizeLanguageCode(language)
      };
      
      devLog('🔍 Persona Generation - Request URL:', url);
      devLog('🔍 Persona Generation - Request body:', JSON.stringify(requestBody));
      devLog('🔍 Persona Generation - BrandId type:', typeof brandId);
      devLog('🔍 Persona Generation - BrandId value:', brandId);
      devLog('🔍 Persona Generation - Language:', normalizeLanguageCode(language));
      
      // Make the API call with no-cache headers
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0'
        },
        body: JSON.stringify(requestBody)
      });
      
      devLog('🔍 Persona Generation - Response status:', response.status);
      devLog('🔍 Persona Generation - Response headers:', Object.fromEntries([...response.headers.entries()]));
      
      if (!response.ok) {
        const errorText = await response.text();
        devLog(`🚨 Persona Generation API error (${response.status}):`, errorText);
        
        // Try to parse the error response as JSON to get detailed error info
        let errorData = null;
        try {
          errorData = JSON.parse(errorText);
          devLog('🚨 Parsed error data:', errorData);
        } catch (parseError) {
          devLog('🚨 Could not parse error response as JSON');
        }
        
        // Enhanced logging for 400 errors to help with debugging
        if (response.status === 400) {
          devLog('🚨 Bad Request (400) Details:');
          devLog('   - Request URL:', url);
          devLog('   - Request Body:', JSON.stringify(requestBody));
          devLog('   - BrandId:', brandId, typeof brandId);
          devLog('   - Language:', normalizeLanguageCode(language));
          devLog('   - User ID:', auth.currentUser?.uid);
          devLog('   - User Email:', auth.currentUser?.email);
          devLog('   - Error Response:', errorText);
          if (errorData) {
            devLog('   - Parsed Error:', errorData);
          }
        }
        
        // Retry logic for 404 errors (which seems to be the temporary issue)
        if (response.status === 404 && attempt < 3) {
          devLog(`🔄 Retrying request due to 404 error... (attempt ${attempt + 1}/3)`);
          await new Promise(resolve => setTimeout(resolve, 1000 * attempt)); // Wait 1s, 2s, then 3s
          return executeRequest(attempt + 1);
        }
        
        // Create a detailed error object that includes both status and parsed data
        const detailedError = new Error(`Failed to generate persona: ${response.status}`);
        (detailedError as any).status = response.status;
        (detailedError as any).errorData = errorData;
        (detailedError as any).errorText = errorText;
        
        // Override the message with a user-friendly version
        detailedError.message = getUserFriendlyErrorMessage(detailedError);
        
        throw detailedError;
      }
      
      const data = await response.json();
      devLog('✅ Successfully generated persona:', data);
      return data;
    };

    try {
      setIsLoading(true);
      setError(null);
      
      const data = await executeRequest();
      
      if (options?.onSuccess) {
        options.onSuccess(data);
      }
      
      return data;
    } catch (error) {
      devLog('🚨 Error in persona generation:', error);
      
      // Enhanced error logging for network issues
      if (error instanceof TypeError && error.message === 'Load failed') {
        devLog('🚨 Network error detected - this could be:');
        devLog('   - CORS issue');
        devLog('   - Network connectivity problem');
        devLog('   - Server not responding');
        devLog('   - Browser blocking the request');
      }
      
      // Enhanced error logging for API errors
      if ((error as any).status) {
        devLog('🚨 API Error Details:');
        devLog('   - Status:', (error as any).status);
        devLog('   - Error Data:', (error as any).errorData);
        devLog('   - Error Text:', (error as any).errorText);
        devLog('   - Original Message:', (error as any).message);
      }
      
      // Create a user-friendly error for the UI
      const userFriendlyError = new Error(getUserFriendlyErrorMessage(error));
      (userFriendlyError as any).originalError = error;
      (userFriendlyError as any).status = (error as any).status;
      (userFriendlyError as any).errorData = (error as any).errorData;
      
      setError(userFriendlyError);
      
      // Always call onError if provided to ensure errors are handled
      if (options?.onError) {
        try {
          options.onError(userFriendlyError);
        } catch (onErrorError) {
          devLog('🚨 Error in onError callback:', onErrorError);
        }
      }
      
      // Re-throw the user-friendly error instead of the original to prevent unhandled rejections
      throw userFriendlyError;
    } finally {
      setIsLoading(false);
    }
  };

  return {
    mutate,
    isLoading,
    error
  };
};

// Function to generate a mock persona (for development only)
const generateDynamicPersona = (brandId: string): PersonaGenerationResponse => {
  devLog(`Generating mock persona for brand ID: ${brandId}`);
  
  return {
    persona: {
      name: "Alex Morgan",
      age: 32,
      gender: "Non-binary",
      location: "Toronto, Canada",
      language: "en",
      jobTitle: "Digital Marketing Specialist",
      income: "$75,000 - $95,000",
      education: "Bachelor's in Communications",
      relationship: "Single",
      imageUrl: "https://ui-avatars.com/api/?name=Alex+Morgan&background=random",
      tags: ["tech-savvy", "early-adopter", "creative"],
      bio: "Alex is a forward-thinking digital marketer with a passion for emerging technologies and innovative campaigns. They thrive in fast-paced environments and are always looking for the next big trend in social media marketing.",
      problems: ["Finding unique ways to stand out in crowded markets", "Keeping up with rapidly changing social media algorithms", "Measuring ROI of creative campaigns"],
      goals: ["Increase brand visibility across emerging platforms", "Master video content creation", "Build authentic connections with audience"],
      motivations: ["Professional recognition", "Creative expression", "Work-life balance"],
      challenges: ["Tight budgets", "Quick turnaround times", "Managing client expectations"],
      values: ["Innovation", "Authenticity", "Creativity"],
      social_platforms: [
        { platform: "TikTok", usage: "Daily", behavior: "Content creation and trend following" },
        { platform: "Instagram", usage: "Daily", behavior: "Visual storytelling and engagement" },
        { platform: "LinkedIn", usage: "Weekly", behavior: "Professional networking and thought leadership" }
      ],
      format: ["Short/Reel", "Stories"],
      brand_affinity: "Alex values brands that demonstrate innovation and authenticity. They're drawn to companies that push creative boundaries and maintain strong ethical values.",
      quote: "The most engaging content isn't created, it's crafted with intention and heart.",
      buying_behavior: "Researches thoroughly before making purchases, values quality over price, and is willing to pay premium for brands that align with their values.",
      brandId: brandId,  // Use the actual brandId passed in
      createdBy: "system",
      createdAt: new Date().toISOString()
    },
    brandId: brandId
  };
}; 